import { useEffect, useState } from "react";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { History as HistoryIcon, ArrowRight } from "lucide-react";
import { formatDate } from "../lib/utils";

function formatValue(v) {
  if (v === "" || v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return String(v);
}

export default function AssessmentHistory({ assessmentId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/assessments/${assessmentId}/history`)
      .then((r) => setItems(r.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [assessmentId]);

  if (loading) return null;
  if (items.length === 0) return null;

  return (
    <Card className="border-slate-200 shadow-sm avoid-break" data-testid="section-history">
      <CardContent className="p-6">
        <div className="mb-4 pb-4 border-b border-slate-100">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">Change log</p>
          <h2 className="text-xl font-semibold tracking-tight text-[#0D1B2A] flex items-center gap-2">
            <HistoryIcon className="w-5 h-5" />Edit history
          </h2>
        </div>
        <ol className="relative border-l-2 border-slate-200 pl-5 space-y-6">
          {items.map((h) => (
            <li key={h.id} className="avoid-break">
              <span className="absolute -left-[7px] w-3 h-3 rounded-full bg-[#1A7F6E] mt-1.5" />
              <div className="flex flex-wrap justify-between gap-2 mb-3">
                <p className="text-sm font-medium text-[#0D1B2A]">
                  {h.editor_name || h.editor_email}
                  <span className="text-slate-500 font-normal"> · {h.editor_email}</span>
                </p>
                <p className="text-xs text-slate-500">{formatDate(h.at)}</p>
              </div>
              <div className="space-y-2">
                {h.changes.map((c) => (
                  <div key={c.field} className="text-sm border border-slate-200 rounded-md p-3 bg-slate-50">
                    <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">{c.label}</p>
                    {c.type === "scalar" ? (
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="line-through text-slate-500">{formatValue(c.before)}</span>
                        <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
                        <span className="text-[#0D1B2A] font-medium">{formatValue(c.after)}</span>
                      </div>
                    ) : (
                      <div className="space-y-1 text-xs">
                        {c.added?.length > 0 && (
                          <div>
                            <span className="text-[#1A7F6E] font-medium">+ added:</span>{" "}
                            <span className="text-slate-700">{c.added.join(", ")}</span>
                          </div>
                        )}
                        {c.removed?.length > 0 && (
                          <div>
                            <span className="text-[#DC2626] font-medium">− removed:</span>{" "}
                            <span className="text-slate-700">{c.removed.join(", ")}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </li>
          ))}
        </ol>
      </CardContent>
    </Card>
  );
}
